import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import { track } from '../lib/track';
import { supabase } from '../lib/supabase';

export default function ClaimSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const sessionId = searchParams.get('session_id');
  const campSlug = searchParams.get('camp_slug');
  // Check both 'plan' and 'plan_name' for compatibility
  const planName = searchParams.get('plan') || searchParams.get('plan_name');
  
  const trackedSessionIdRef = useRef<string | null>(null);

  // If this is a parent plan, redirect to the correct success page
  useEffect(() => {
    const planNameFromUrl = searchParams.get('plan') || searchParams.get('plan_name');
    if (planNameFromUrl === 'family-sync') {
      // Redirect parent plans to the dedicated payment success page
      navigate(`/payment/success?session_id=${sessionId || ''}&plan_name=family-sync`, { replace: true });
      return;
    }
  }, [searchParams, navigate, sessionId]);

  useEffect(() => {
    const processPaymentSuccess = async () => {

      // Check for camp slug from URL params or sessionStorage
      const slugFromParams = campSlug;
      const slugFromStorage = sessionStorage.getItem('pending_camp_slug');
      const planFromStorage = sessionStorage.getItem('pending_plan_name');
      
      const finalCampSlug = slugFromParams || slugFromStorage;
      const finalPlanName = planName || planFromStorage || 'trailhead';

      // If we have a camp slug, update the camp status and redirect (owner flow)
      if (finalCampSlug) {
        try {
          // Get the current user
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user) {
            // Update the camp status immediately
            const { error } = await supabase
              .from('camps')
              .update({
                claimed: true,
                owner_id: user.id,
                plan_id: finalPlanName,
                claimed_at: new Date().toISOString()
              })
              .eq('slug', finalCampSlug);

            if (error) {
              console.error('Error updating camp status:', error);
            } else {
              console.log('Camp status updated successfully');
            }
          }

          const campIdFromStorage = sessionStorage.getItem('pending_claim_camp_id');
          if (sessionId && trackedSessionIdRef.current !== sessionId) {
            trackedSessionIdRef.current = sessionId;
            track('purchase_completed', {
              persona: 'camp_owner',
              product_type: 'camp_claim',
              camp_id: campIdFromStorage || undefined,
              stripe_session_id: sessionId || '',
              order_id: sessionId || '',
              currency: 'USD',
            });
          }
          sessionStorage.removeItem('pending_camp_slug');
          sessionStorage.removeItem('pending_plan_name');
          sessionStorage.removeItem('pending_claim_camp_slug');
          sessionStorage.removeItem('pending_claim_camp_id');
          sessionStorage.removeItem('stripe_checkout_initiated');
          navigate(`/camps/${finalCampSlug}?payment_success=true&plan=${finalPlanName}`, { replace: true });
          return;
        } catch (error) {
          console.error('Error processing payment success:', error);
          // Still redirect even if update fails
          navigate(`/camps/${finalCampSlug}?payment_success=true&plan=${finalPlanName}`, { replace: true });
          return;
        }
      }

      // Otherwise, show the success page
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 2000);

      return () => clearTimeout(timer);
    };

    processPaymentSuccess();
  }, [campSlug, planName, navigate]);

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="max-w-2xl mx-auto text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              {campSlug ? 'Redirecting to your camp...' : 'Processing Your Payment...'}
            </h1>
            <p className="text-gray-600">
              {campSlug 
                ? 'Taking you to your camp page with success details.' 
                : 'Please wait while we confirm your camp claim.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <SEO
        title="Payment Successful | CampWhiz"
        description="Your camp claim payment has been processed successfully."
        url="/claim/success"
        noindex={true}
      />
      
      <div className="page-content py-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-8">
            <CheckCircleIcon className="h-24 w-24 text-green-500 mx-auto mb-6" />
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Payment Successful! 🎉
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              Your camp has been successfully claimed and your payment has been processed.
            </p>
            {sessionId && (
              <p className="text-sm text-gray-500 mb-6">
                Session ID: {sessionId}
              </p>
            )}
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-green-800 mb-3">
              What's Next?
            </h2>
            <ul className="text-left text-green-700 space-y-2">
              <li>✅ Your camp is now claimed and active</li>
              <li>✅ You can manage your camp listing</li>
              <li>✅ Families can now find and contact you</li>
              <li>✅ You'll receive email confirmations shortly</li>
            </ul>
          </div>

          <div className="space-y-4">
            <Link
              to="/my-camps"
              className="inline-block bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Go to My Camps
            </Link>
            <div>
              <Link
                to="/"
                className="text-primary hover:text-primary/80 font-medium"
              >
                Return to Homepage
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
